import { DiscountType } from '@repo/common';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@repo/shadcn';
import { Edit, Loader2Icon } from 'lucide-react';
import { useState } from 'react';

import { SelectFilter } from '@components/ui/search-filter';

import { formatCurrencyInBDT } from '@/utils';

import { useCartContext } from './cart.context';

export const OrderSummary = () => {
    const { activeCart } = useCartContext();

    const info = activeCart?.data?.info;

    const subTotal = formatCurrencyInBDT(info?.sub_total_price || 0);
    const tax = formatCurrencyInBDT(info?.tax || 0);
    const totalPrice = formatCurrencyInBDT(info?.total_price || 0);
    const discountType = info?.discount_type;
    const discountValue = info?.discount_value || 0;
    const calculatedDiscountValue =
        discountType === DiscountType.Percentage
            ? ((info?.sub_total_price || 0) * discountValue) / 100
            : discountValue;

    // const individualDiscount =
    //     activeCart?.data?.items?.reduce((acc, item) => {
    //         const itemTotalPrice = (item.price || 0) * (item.quantity || 0);
    //
    //         const itemTotalAfterDiscount =
    //             itemTotalPrice - (item.total_price || 0);
    //
    //         return acc + itemTotalAfterDiscount;
    //     }, 0) || 0;

    const individualDiscount = info?.individual_discount || 0;

    const renderDiscountLabel = () => {
        if (discountType === DiscountType.Percentage && discountValue > 0) {
            return `Discount (${discountValue}%)`;
        }

        return 'Discount';
    };

    return (
        <Card>
            <CardHeader className="px-3 py-3">
                <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="px-3 py-3">
                <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{subTotal}</span>
                </div>

                <div className="mt-2 flex justify-between">
                    <span>Vat</span>
                    <span>{tax}</span>
                </div>

                <div className="mt-2 flex justify-between">
                    <span>Individual Discount</span>
                    <span>{formatCurrencyInBDT(individualDiscount)}</span>
                </div>

                <EditDiscount
                    label={renderDiscountLabel()}
                    value={formatCurrencyInBDT(calculatedDiscountValue)}
                />

                <div className="mt-2 flex justify-between border-t pt-2 font-bold">
                    <span>Total</span>
                    <span>{totalPrice}</span>
                </div>
            </CardContent>
        </Card>
    );
};

OrderSummary.displayName = 'OrderSummary';

const EditDiscount = ({ label, value }: { label: string; value: string }) => {
    const { activeCart, updateDiscount, isUpdateCartInfoLoading } =
        useCartContext();

    const [isOpen, setIsOpen] = useState(false);

    const cartDiscountType =
        activeCart?.data?.info?.discount_type ?? DiscountType.Percentage;
    const cartDiscountValue = activeCart?.data?.info?.discount_value ?? 0;

    const [discountType, setDiscountType] = useState(cartDiscountType);
    const [discountInput, setDiscountInput] = useState(
        cartDiscountValue === 0 ? '' : cartDiscountValue.toString(),
    );

    const handleUpdateDiscount = async () => {
        const value = parseFloat(discountInput) || 0;
        const type = discountType;

        //    check if value is changed
        if (value === cartDiscountValue && type === cartDiscountType) {
            return;
        }

        // check for negative value
        if (value < 0) {
            alert('Discount value cannot be negative');
            return;
        }

        await updateDiscount(type, value).then(() => {
            resetFields();
            setIsOpen(false);
        });
    };

    const handleClearDiscount = () => {
        setDiscountType(DiscountType.Percentage);
        setDiscountInput('');
        updateDiscount(DiscountType.Percentage, 0).then(() => {
            resetFields();
            setIsOpen(false);
        });
    };

    const resetFields = () => {
        setDiscountType(cartDiscountType);
        setDiscountInput(
            cartDiscountValue === 0 ? '' : cartDiscountValue.toString(),
        );
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            resetFields();
            setIsOpen(false);
        } else {
            resetFields();
            setIsOpen(true);
        }
    };

    return (
        <Popover onOpenChange={handleOpenChange} open={isOpen}>
            <PopoverTrigger asChild>
                <button
                    type={'button'}
                    className={
                        'mt-2 flex w-full items-center justify-between gap-3 text-left focus:outline-none'
                    }
                >
                    <span className={'flex items-center gap-1'}>
                        <span>{label}</span>
                        <Edit className={'size-4'} />
                    </span>
                    <span>{value}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
                <div className={'space-y-2'}>
                    <h3 className={'text-lg font-bold'}>Update Discount</h3>

                    <div className={'grid grid-cols-2 items-center gap-1'}>
                        <span>Discount Type: </span>
                        <SelectFilter
                            placeholder="Fixed"
                            className="scale-[0.88]"
                            onChange={(value) => {
                                setDiscountType(value as DiscountType);
                            }}
                            value={discountType}
                            options={[
                                {
                                    label: 'Fixed',
                                    value: DiscountType.Fixed,
                                },
                                {
                                    label: 'Percentage',
                                    value: DiscountType.Percentage,
                                },
                            ]}
                        />
                    </div>
                    <div className={'grid grid-cols-2 items-center gap-1'}>
                        <span>Discount Value: </span>
                        <Input
                            type={'number'}
                            value={discountInput}
                            onChange={(e) => setDiscountInput(e.target.value)}
                            className={
                                'h-[34px] w-full p-0 px-0.5 py-0 text-right'
                            }
                        />
                    </div>

                    <div className={'flex justify-end gap-4 pt-2'}>
                        <Button
                            type={'button'}
                            variant={'destructive'}
                            onClick={() => handleClearDiscount()}
                        >
                            Clear
                        </Button>
                        <Button
                            disabled={isUpdateCartInfoLoading}
                            type={'button'}
                            onClick={handleUpdateDiscount}
                        >
                            {isUpdateCartInfoLoading ? (
                                <Loader2Icon className={'animate-spin'} />
                            ) : (
                                'Update'
                            )}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
